import React, { RefObject, useContext, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button, Card, CardContent, CardHeader, CardTitle, ChevronRight, RequestFeedback } from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { ProfileContext } from 'Deer/pages/Profile/hooks/ProfileContext';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import noListData from 'assets/assetsV1/illustrations/deerIllustrations/noListData.svg';

import { pushFeedbackParams } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

import { EmployeeData } from './EmployeeData';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface Props {
  cfTableData?: any[];
  gridRef?: RefObject<AgGridReact>;
}

export const FeedbackTable = ({ cfTableData, gridRef }: Props) => {
  const history = useHistory();
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const employeeContext = useContext(ProfileContext);
  const cfDataToPass = cfTableData?.slice(0, employeeContext?.id ? cfTableData?.length : 3);
  const { search } = useLocation();

  const handleClickViewAllFeedback = () => {
    const urlToPush = L3_PATHS['my-data'].develop.feedback;

    const queryParams = new URLSearchParams(search);
    queryParams.set('feedbackView', 'RECEIVED');

    history.push({
      pathname: urlToPush,
      search: queryParams.toString(),
    });
  };

  const columnDefs = useMemo(
    () => [
      {
        colId: 'employee',
        field: 'employee',
        id: 'employee',
        suppressHeaderMenuButton: true,
        width: 497,
        flex: 1,
        cellRenderer: EmployeeData,
        valueGetter: (params) => params?.data,
      },
    ],
    []
  );

  const rowClickHandler = ({ data }) => {
    const { uuid: feedbackId } = data.requestDetails ?? data.comment ?? {};
    const formType = 'SHOW_RECEIVED';
    // @ts-expect-error in api
    pushFeedbackParams({
      history,
      formType,
      typeMapped: true,
      feedbackId,
    });
  };

  if (!cfTableData || cfTableData?.length === 0) {
    return (
      <Card
        style={{
          minHeight: '418px',
          marginRight: '8px',
          height: '100%',
        }}
      >
        <CardContent>
          <EmptyScreen
            illustrationSrc={noListData}
            heading="No feedback yet"
            isSmallTextSize
            description="Request feedback from team members to see progress here"
            illustrationStyle={{
              marginTop: '80px',
            }}
            // eslint-disable-next-line react/no-unstable-nested-components
            CTAComp={() => (
              <Button
                iconLeft={RequestFeedback}
                onClick={() =>
                  // @ts-expect-error-here
                  pushFeedbackParams({
                    history,
                    formType: 'REQUESTED',
                    newForm: true,
                    empId: (employeeContext?.id ? employeeContext?.id : loggedInEmployeeId) ?? null,
                  })
                }
                variant="outline"
              >
                Request Feedback
              </Button>
            )}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ height: '100%' }}>
      <CardHeader>
        <CardTitle>Feedback received</CardTitle>
      </CardHeader>

      <CardContent>
        <Box
          minHeight="388px"
          style={
            employeeContext?.id
              ? {
                  maxHeight: '420px',
                  overflowY: 'scroll',
                }
              : {}
          }
        >
          <AgGridTable
            ref={gridRef}
            pagination
            suppressPaginationPanel
            domLayout="autoHeight"
            animateRows={false}
            rowData={cfDataToPass}
            columnDefs={columnDefs}
            headerHeight={0}
            onRowClicked={rowClickHandler}
            containerAutoHeight
            rowHeight={127}
            isOverviewPage
          />
        </Box>
      </CardContent>
      {cfTableData?.length > 1 && !employeeContext?.id && (
        <Flex justifyContent="center" width="full" marginBottom="-10px">
          <Button onClick={handleClickViewAllFeedback} variant="text" iconRight={ChevronRight}>
            View All Feedback
          </Button>
        </Flex>
      )}
    </Card>
  );
};
